/**
 * Rota Protegida - Verifica autenticação e aprovação
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, isAdmin, isApproved } = useAuth();
  const [checkingWhatsApp, setCheckingWhatsApp] = useState(true);
  const [hasWhatsApp, setHasWhatsApp] = useState(false);

  // Verificar se usuário tem WhatsApp cadastrado
  useEffect(() => {
    const checkWhatsApp = async () => {
      if (!user || loading) {
        setCheckingWhatsApp(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/user/${user.uid}`
        );

        if (response.ok) {
          const userData = await response.json();
          setHasWhatsApp(!!userData.whatsapp);
        } else {
          setHasWhatsApp(false);
        }
      } catch (error) {
        console.error('Erro ao verificar WhatsApp:', error);
        setHasWhatsApp(false);
      } finally {
        setCheckingWhatsApp(false);
      }
    };

    checkWhatsApp();
  }, [user, loading]);

  // Mostrar loading enquanto verifica autenticação ou WhatsApp
  if (loading || checkingWhatsApp) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  // Se não estiver logado, redirecionar para login
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Se não tiver WhatsApp cadastrado, redirecionar para login onde o modal será exibido
  if (!hasWhatsApp) {
    console.log('⚠️ Usuário sem WhatsApp em ProtectedRoute - redirecionando para login');
    return <Navigate to="/" replace />;
  }

  // Se for admin, sempre permitir acesso
  if (isAdmin) {
    return <>{children}</>;
  }

  // Se não for admin e não estiver aprovado, redirecionar para página de aguardando
  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  // Usuário aprovado pode acessar
  return <>{children}</>;
};

export default ProtectedRoute;
